import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  Divider,
  InputAdornment,
  Menu,
  MenuItem,
  useTheme,
  alpha
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiIcon,
  MoreVert as MoreVertIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Announcement as AnnouncementIcon
} from '@mui/icons-material';
import { Chat, Message, UserRole } from '../../utils/types';
import { sendMessage, subscribeToMessages, markMessageAsRead } from '../../services/chatService';
import { createMessageNotification } from '../../services/teamNotificationService';
import MessageBubble from './MessageBubble';
import { formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';

interface ChatWindowProps {
  chat: Chat;
  currentUserId: string;
  currentUserName: string;
  currentUserRole?: UserRole;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  chat,
  currentUserId,
  currentUserName,
  currentUserRole
}) => {
  const theme = useTheme();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // تحميل الرسائل والاستماع للتحديثات
  useEffect(() => {
    if (!chat.id) return;

    setLoading(true);
    setMessages([]);

    const unsubscribe = subscribeToMessages(chat.id, (updatedMessages) => {
      setMessages(updatedMessages);
      setLoading(false);
      
      // تمييز الرسائل كمقروءة
      updatedMessages.forEach(message => {
        if (message.senderId !== currentUserId && !message.readBy.includes(currentUserId)) {
          markMessageAsRead(message.id, currentUserId);
        }
      });
    });

    return () => unsubscribe();
  }, [chat.id, currentUserId]);

  // التمرير إلى أسفل عند وصول رسائل جديدة
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // إرسال رسالة جديدة
  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      
      const messageData = {
        senderId: currentUserId,
        senderName: currentUserName,
        senderRole: currentUserRole || UserRole.CASHIER,
        content: newMessage.trim(),
        type: 'text' as const,
        chatId: chat.id,
        isRead: false,
        readBy: [currentUserId], // المرسل يعتبر قارئ للرسالة
        isEdited: false,
        isDeleted: false
      };

      await sendMessage(messageData);

      // إرسال إشعارات للمشاركين الآخرين
      const otherParticipants = chat.participants.filter(p => p.userId !== currentUserId);
      for (const participant of otherParticipants) {
        await createMessageNotification(
          participant.userId,
          currentUserName,
          chat.name,
          newMessage.trim(),
          chat.id
        );
      }

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  // التعامل مع الضغط على Enter
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // الحصول على أيقونة نوع المحادثة
  const getChatTypeIcon = () => {
    switch (chat.type) {
      case 'private':
        return <PersonIcon />;
      case 'group':
        return <GroupIcon />;
      case 'announcement':
        return <AnnouncementIcon />;
      default:
        return <GroupIcon />;
    }
  };

  // الحصول على اسم المحادثة المناسب
  const getChatDisplayName = () => {
    if (chat.type === 'private') {
      const otherParticipant = chat.participants.find(p => p.userId !== currentUserId);
      return otherParticipant?.userName || 'مستخدم غير معروف';
    }
    return chat.name;
  };

  // فتح قائمة الخيارات
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // إغلاق قائمة الخيارات
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // تنسيق وقت آخر نشاط
  const formatLastActivity = () => {
    try {
      return formatDistanceToNow(chat.lastActivity, { 
        addSuffix: true, 
        locale: ar 
      });
    } catch (error) {
      return 'الآن';
    }
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        height: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {/* رأس المحادثة */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.05)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 48,
                height: 48
              }}
            >
              {getChatTypeIcon()}
            </Avatar>
            
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {getChatDisplayName()}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {chat.type !== 'private' && (
                  <Chip
                    size="small"
                    label={`${chat.participants.length} عضو`}
                    sx={{ height: 20, fontSize: '0.75rem' }}
                  />
                )}
                
                <Typography variant="caption" color="text.secondary">
                  آخر نشاط {formatLastActivity()}
                </Typography>
              </Box>
            </Box>
          </Box>

          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      {/* منطقة الرسائل */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 1,
          bgcolor: alpha(theme.palette.background.default, 0.5)
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              جاري تحميل الرسائل...
            </Typography>
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              لا توجد رسائل بعد. ابدأ المحادثة!
            </Typography>
          </Box>
        ) : (
          <>
            {messages.map((message, index) => {
              const showAvatar = index === 0 || messages[index - 1].senderId !== message.senderId;
              const showTimestamp = index === messages.length - 1 || 
                messages[index + 1].senderId !== message.senderId ||
                (messages[index + 1].timestamp.getTime() - message.timestamp.getTime()) > 300000; // 5 دقائق

              return (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isOwn={message.senderId === currentUserId}
                  showAvatar={showAvatar}
                  showTimestamp={showTimestamp}
                />
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* شريط إدخال الرسالة */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="اكتب رسالة..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={sending}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton size="small" disabled>
                    <AttachFileIcon />
                  </IconButton>
                  <IconButton size="small" disabled>
                    <EmojiIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sending}
                    color="primary"
                  >
                    <SendIcon />
                  </IconButton>
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
            }
          }}
        />
      </Box>

      {/* قائمة الخيارات */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleMenuClose}>
          <Typography variant="body2">معلومات المحادثة</Typography>
        </MenuItem>
        
        {chat.type === 'group' && (
          <MenuItem onClick={handleMenuClose}>
            <Typography variant="body2">إدارة الأعضاء</Typography>
          </MenuItem>
        )}
        
        <MenuItem onClick={handleMenuClose}>
          <Typography variant="body2">
            {chat.settings.muteNotifications ? 'إلغاء كتم الإشعارات' : 'كتم الإشعارات'}
          </Typography>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          <Typography variant="body2">مغادرة المحادثة</Typography>
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default ChatWindow;
